import { useTranslation } from 'next-i18next/pages';
import Image from 'next/image';
import React from 'react';

import useSelectImage from '@/hooks/apps/useSelectImage';
import { useMediaQuery } from '@/hooks/shared/useMediaQuery';

import Area from '@/components/apps/layouts/shared/Area';
import SectionTitle from '@/components/apps/ui/title/SectionTitle';
import { AniSlideUp } from '@/components/shared/animations/basic';

import PcImageSrc from '@/images/apps/solution/industrial/sec_02_img_01.svg';
import PcEnImageSrc from '@/images/apps/solution/industrial/sec_02_img_01_en.svg';
import MobileEnImageSrc from '@/images/apps/solution/industrial/sec_02_img_01_en_m.svg';
import MobileImageSrc from '@/images/apps/solution/industrial/sec_02_img_01_m.svg';

import theme from '@/styles/theme';

const LIST_BASE_CLASS =
  'flex flex-col gap-[4px] px-0 py-[24px] lg:grid lg:grid-cols-[calc(4/12*100%)_calc(8/12*100%)] lg:py-[40px]';

/**
 * 원본에서 `.no-border` 블록이 base 의 `border-bottom: 1px solid`
 * 뒤에 선언돼 마지막 항목의 테두리를 지웠다.
 * Tailwind 를 important 로 임포트하고 있어 같은 속성을 두 번 내보내면
 * 승자가 CSS 출력 순서로 정해지므로, 분기를 여기서 계산해 하나만 붙인다.
 */
function listBorderClass(isLast: boolean) {
  return isLast ? '' : 'border-b border-b-apps-brandGray50';
}

export default function SolutionMachineDetectionSection() {
  const { t } = useTranslation('common');
  const isDesktop = useMediaQuery({
    query: `(min-width: ${theme`screens.lg`})`,
  });

  // Detection 섹션 데이터
  const SOLUTION_MACHINE_DETECTION_SECTION_DATA = [
    {
      title: t('객관적 · 일관적인 품질 관리'),
      description: t(
        '기존에 검수자마다 다르던 품질 평가 기준을 일정하고 객관적인 Listen AI의 품질평가로 바꿀 수 있어요. 또한, 가장 우수한 품질 평가 기준을 AI에게 학습시켜 24시간 우수한 품질 기준으로 제품을 생산할 수 있어요.',
      ),
    },
    {
      title: t('현장 노이즈 속에도 일정한 AI'),
      description: t(
        '공장 현장의 노이즈가 있더라도 다양한 소음 제거 기술을 활용해 타겟음만 정확하게 탐지하고 판단할 수 있어요.',
      ),
    },
    {
      title: t('C/T를 지켜주는 유연한 생산 관리'),
      description: t(
        '경량화 · 효율화된 Listen AI가 기존 생산 라인의 Cycle Time 지연 없이도 분석 가능해요.',
      ),
    },
  ];
  return (
    <section
      className="bg-apps-brandGrayBlack pt-[clamp(100px,10.417vw,200px)]"
      data-analytics-section="quality_management"
    >
      <Area>
        <SectionTitle
          title={t('Listen AI가 만드는 객관적인 품질 관리')}
          description={t(
            '주관적인(Subjective) 판단 기준을 Listen AI를 통해 객관적이고(Objective), 구체적인 수치로써 품질 관리가 가능해져요.',
          )}
          color="lighter"
        />
      </Area>

      {/* image-set() 배경은 유틸리티로 표현할 수 없어 globals.css 의 클래스로 남긴다. */}
      <div className="industrial-detection-bg">
        <Area>
          <div className="relative mx-auto mt-[40px] mb-0 aspect-[335/632] w-full max-w-[600px] lg:mx-0 lg:mt-[80px] lg:mb-[40px] lg:aspect-[1480/332] lg:max-w-full">
            <Image
              src={useSelectImage({
                pcImgSrc: PcImageSrc,
                mobileImgSrc: MobileImageSrc,
                pcEnImgSrc: PcEnImageSrc,
                mobileEnImgSrc: MobileEnImageSrc,
                isDesktop,
              })}
              alt={t('수집한 소리에서 노이즈를 제거한 뒤 정상과 경고로 판정하는 흐름도')}
              fill
            />
          </div>
          <div>
            {SOLUTION_MACHINE_DETECTION_SECTION_DATA.map((item, index) => (
              <AniSlideUp key={index}>
                <div
                  className={`${LIST_BASE_CLASS} ${listBorderClass(
                    index ===
                      SOLUTION_MACHINE_DETECTION_SECTION_DATA.length - 1,
                  )}`}
                >
                  <h4 className="text-[22px] font-bold leading-[160%] tracking-[-0.44px] text-apps-coreGrayWhite lg:text-[28px] lg:leading-[140%]">
                    {item.title}
                  </h4>
                  {/* 원본에 base font-size 가 없어 body 값을 상속한다. lg 에서만 18px 로 커진다. */}
                  <p className="font-medium leading-[140%] tracking-[-0.32px] text-apps-brandGray600 lg:text-[18px] lg:leading-[160%] lg:tracking-[-0.36px]">
                    {item.description}
                  </p>
                </div>
              </AniSlideUp>
            ))}
          </div>
        </Area>
      </div>
    </section>
  );
}
